import { AppSidebar } from '@/components/sidenav/app-sidebar';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb';
import { SidebarInset, SidebarProvider } from '@/components/ui/sidebar';
import { AppProviders } from '@/context/app-providers';
import { AgentPanelProvider } from '@/context/agent-panel.context';
import { AgentStateProvider } from '@/context/agent-state.context';
import { AgentContainerProvider } from '@/context/agent-container.context';
import { AnnotationCanvasProvider } from '@/context/annotation-canvas.context';
import { BackgroundJobsPanel } from '@/components/background-jobs/BackgroundJobsPanel';
import { TodoListProvider } from '@/components/common/chat/todo-list';
import { ChatAgentRenderer } from '@/components/common/ChatAgentRenderer';
import { HotkeysProvider } from 'react-hotkeys-hook';
import { EntityQuickFind } from '@/components/common/EntityQuickFind';
import { AnnotationCanvas } from '@/components/common/chat/AnnotationCanvas';
import ErrorBoundary from 'src/components/ErrorBoundary';
import {
  createRoute,
  notFound,
  Outlet,
  redirect,
  useRouterState,
} from '@tanstack/react-router';
import { Route as rootRoute, type RouterContext } from 'src/pages/__root';
import NotFoundPage from '../NotFoundPage';
import type { ProductModule } from '@/hooks/organization-config/useOrganizationEnabledModules';
import type { FeatureFlagKey } from '@erp/shared-types';
import {
  isBooleanFeatureFlagEnabled,
  useFeatureFlag,
} from '@/integrations/feature-flags/feature-flags';
import React, { useContext } from 'react';
import { Link } from '@tanstack/react-router';
import { AuthContext } from '@/context/auth.context';
import { useViewMode } from '@/context/view-mode.context';
import { isAdmin } from '@/utils/user';
import { WarningIcon } from '@phosphor-icons/react';
import config from '@/config';
import { Button } from '@/components/ui/button';
import paths from '@/paths';
import { Action, Subject } from '@erp/authz';
import { useCan } from '@/hooks/useCan';
import { T } from 'gt-react';
import { FeedbackButton } from '@/components/common/FeedbackButton';
import { DeploymentEnvBanner } from '@/components/common/DeploymentEnvBanner';
import { FloatingAgentPanel } from '@/components/common/FloatingAgentPanel';
import { HelpMenuProvider } from '@/components/common/HelpMenu';
import { NotificationBell } from '@/components/notifications/NotificationBell';
import { useSessionHeartbeat } from '@/hooks/useSessionHeartbeat';
import { ToolsMenu } from '@/components/header/tools-menu';

export function requireModules({
  allOf,
  oneOf,
  context,
}: {
  context: RouterContext;
  allOf?: ProductModule[];
  oneOf?: ProductModule[];
}): void {
  const { isModuleEnabled } = context;

  // require all modules in modulesRequired
  if (allOf) {
    for (const module of allOf) {
      if (!isModuleEnabled(module)) {
        throw notFound({
          routeId: '/authenticated',
          data: {
            message: `Missing required module: ${module}`,
          },
        });
      }
    }
  }

  if (!oneOf) {
    return;
  }

  const hasAtLeastOneModuleEnabled = oneOf?.some(isModuleEnabled);
  if (!hasAtLeastOneModuleEnabled) {
    throw notFound({
      routeId: '/authenticated',
      data: {
        message: `Missing at least one of the following modules: ${oneOf?.join(
          ', ',
        )}`,
      },
    });
  }
}

/**
 * Require a feature flag to be enabled to reach a route. Pair with
 * `access.featureFlag` (which hides the nav entry) so this also blocks
 * direct-URL access. Reads the synchronous flag value (PostHog + local
 * overrides) so it works inside `beforeLoad`.
 */
export function requireFeatureFlag(flag: FeatureFlagKey): void {
  if (!isBooleanFeatureFlagEnabled(flag, false)) {
    throw notFound({
      routeId: '/authenticated',
      data: {
        message: `Feature not enabled: ${flag}`,
      },
    });
  }
}

/**
 * Blocks the /preview/* pitch pages when the `hide-feature-signups` kill
 * switch is on. Absent/off (the default) leaves the waitlist pages open.
 */
export function requireFeatureSignupsVisible(): void {
  if (isBooleanFeatureFlagEnabled('hide-feature-signups', false)) {
    throw redirect({ to: '/' });
  }
}

/**
 * Where to send a user who can't reach the real Agents / Workflows page.
 * Points at the pitch page unless signups are hidden, in which case it falls
 * back to the pre-teaser bounce destination (`/`).
 */
export function featureSignupFallbackRedirect(
  previewPath: '/preview/agents' | '/preview/workflows',
): string {
  return isBooleanFeatureFlagEnabled('hide-feature-signups', false)
    ? '/'
    : previewPath;
}

/**
 * Require user to have a specific permission to access a route.
 *
 * This performs an async permission check against the backend.
 * Use this in beforeLoad for routes that require specific permissions.
 *
 * @example
 * ```typescript
 * beforeLoad: async ({ context }) => {
 *   await requirePermission({
 *     context,
 *     action: Action.Update,
 *     subject: Subject.UserGroup,
 *   });
 * },
 * ```
 */
export async function requirePermission({
  context,
  action,
  subject,
  redirectTo = '/unauthorized',
}: {
  context: RouterContext;
  action: Action;
  subject: Subject;
  redirectTo?: string;
}): Promise<void> {
  const { authzClient } = context;

  const allowed = await authzClient.can(action, subject);

  if (!allowed) {
    throw redirect({
      to: redirectTo,
    });
  }
}

/**
 * Require user to have at least one of the specified permissions.
 *
 * @example
 * ```typescript
 * beforeLoad: async ({ context }) => {
 *   await requireAnyPermission({
 *     context,
 *     permissions: [
 *       { action: Action.Read, subject: Subject.UserGroup },
 *       { action: Action.Update, subject: Subject.UserGroup },
 *     ],
 *   });
 * },
 * ```
 */
export async function requireAnyPermission({
  context,
  permissions,
  redirectTo = '/unauthorized',
}: {
  context: RouterContext;
  permissions: { action: Action; subject: Subject }[];
  redirectTo?: string;
}): Promise<void> {
  const { authzClient } = context;

  for (const { action, subject } of permissions) {
    const allowed = await authzClient.can(action, subject);
    if (allowed) {
      return; // At least one permission is granted
    }
  }

  // No permissions granted
  throw redirect({
    to: redirectTo,
  });
}

/**
 * Require user to be a non-restricted user.
 * Restricted users can only access shared links and shared reports.
 */
export function requireNonRestrictedUser({
  context,
  location,
}: {
  context: RouterContext;
  location: { pathname: string };
}): void {
  const { auth } = context;
  const isRestrictedUser = auth.currentUser?.isRestrictedUser ?? false;

  if (!isRestrictedUser) {
    return;
  }

  // Allow access to shared links, shared reports, and user preferences (so they can change passwords etc)
  const allowedPaths = ['/shared-links', '/shared/', '/settings/user'];
  const isAllowedPath = allowedPaths.some(
    (path) => location.pathname === path || location.pathname.startsWith(path),
  );

  if (!isAllowedPath) {
    throw redirect({
      to: '/shared-links',
    });
  }
}

/**
 * Require user to be an admin.
 * Redirects non-admin users to the specified path (defaults to '/').
 *
 * @example
 * ```typescript
 * beforeLoad: ({ context }) => {
 *   requireAdmin({ context });
 * },
 * ```
 *
 * @example
 * ```typescript
 * beforeLoad: ({ context }) => {
 *   requireAdmin({
 *     context,
 *     redirectTo: '/inbound-emails',
 *   });
 * },
 * ```
 */
export function requireAdmin({
  context,
  redirectTo = '/',
}: {
  context: RouterContext;
  redirectTo?: string;
}): void {
  const { auth } = context;
  if (!auth.currentUser?.isAdmin) {
    throw redirect({
      to: redirectTo,
    });
  }
}

export const Route = createRoute({
  id: 'authenticated',
  getParentRoute: () => rootRoute,
  beforeLoad: async (ctx) => {
    const { auth } = ctx.context;
    if (!auth.currentUser) {
      throw redirect({
        to: '/login',
        search: {
          // Save current location for redirect after login
          redirect: ctx.location.href,
        },
      });
    }

    // Redirect restricted users to shared-links if they try to access other routes
    requireNonRestrictedUser({
      context: ctx.context,
      location: ctx.location,
    });
  },
  component: () => (
    <AuthenticatedLayout>
      <Outlet />
    </AuthenticatedLayout>
  ),
  notFoundComponent: () => (
    <AuthenticatedLayout>
      <NotFoundPage />
    </AuthenticatedLayout>
  ),
});

function HeaderActions() {
  const { currentUser } = useContext(AuthContext);
  const { viewMode } = useViewMode();
  const admin = isAdmin(currentUser, viewMode);
  const isRestrictedUser = currentUser?.isRestrictedUser ?? false;
  const [canReadBackgroundAgents] = useCan(
    Action.Read,
    Subject.BackgroundAgent,
  );
  // Kill switch: when ON, restore pre-teaser header (signup-only tabs gone,
  // Workflows admin-only). Absent/OFF keeps the growth tabs.
  const { enabled: hideFeatureSignups } = useFeatureFlag(
    'hide-feature-signups',
    false,
  );

  const showAgents =
    canReadBackgroundAgents || (!hideFeatureSignups && !isRestrictedUser);
  const showTasks = !hideFeatureSignups && !isRestrictedUser;
  const showWorkflows = admin || (!hideFeatureSignups && !isRestrictedUser);

  return (
    <div className="flex items-center gap-1 px-4">
      {!isRestrictedUser && (
        <>
          {showAgents && (
            <Button variant="ghost" size="sm" asChild>
              <Link
                to={
                  canReadBackgroundAgents
                    ? paths.authenticated.backgroundAgents.list
                    : paths.authenticated.featurePreviews.agents
                }
              >
                <T context="top navbar agents link label">Agents</T>
              </Link>
            </Button>
          )}
          {showTasks && (
            <Button variant="ghost" size="sm" asChild>
              <Link to={paths.authenticated.featurePreviews.tasks}>
                <T context="top navbar tasks link label">Tasks</T>
              </Link>
            </Button>
          )}
          <ToolsMenu hideFeatureSignups={hideFeatureSignups} />
          {showWorkflows && (
            <Button variant="ghost" size="sm" asChild>
              <Link
                to={
                  admin
                    ? paths.authenticated.workflows.list
                    : paths.authenticated.featurePreviews.workflows
                }
              >
                <T context="top navbar workflow link label">Workflows</T>
              </Link>
            </Button>
          )}
        </>
      )}
      <NotificationBell />
    </div>
  );
}

function AuthenticatedLayout({ children }: { children: React.ReactNode }) {
  // Track time-in-app: periodic activity heartbeat while the tab is visible.
  useSessionHeartbeat();

  const matches = useRouterState({ select: (s) => s.matches });
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const isShipAndDebit =
    pathname === '/ship-and-debit' || pathname.startsWith('/ship-and-debit/');

  const breadcrumbs = matches
    .map(({ pathname, loaderData }) => {
      // A route may point its crumb at a different path (e.g. a detail page
      // linking its crumb back to the list) via `crumbTo`; default to its own.
      const crumbTo =
        loaderData && 'crumbTo' in loaderData ? loaderData.crumbTo : undefined;
      return {
        title: loaderData?.crumb,
        path: typeof crumbTo === 'string' ? crumbTo : pathname,
      };
    })
    .filter((pathAndTitle) => pathAndTitle.title !== undefined);

  return (
    <HotkeysProvider>
      <AppProviders>
        <AgentPanelProvider>
          <AgentStateProvider>
            <TodoListProvider>
              <AgentContainerProvider>
                <HelpMenuProvider>
                  <AnnotationCanvasProvider>
                    <SidebarProvider>
                      <AppSidebar />
                      <SidebarInset
                        className="h-svh overflow-hidden transition-[padding] duration-200 ease-in-out"
                        style={{
                          paddingRight: 'var(--agent-panel-width, 0px)',
                        }}
                      >
                        <header className="bg-sidebar flex h-16 shrink-0 items-center justify-between gap-2 border-b border-sidebar-border transition-[width,height] ease-linear group-has-data-[collapsible=icon]/sidebar-wrapper:h-12">
                          <div className="flex items-center gap-2 px-4">
                            <Breadcrumb>
                              <BreadcrumbList>
                                {breadcrumbs.map((breadcrumb, index) => (
                                  <React.Fragment key={breadcrumb.path}>
                                    <BreadcrumbItem>
                                      <BreadcrumbLink asChild>
                                        <Link to={breadcrumb.path}>
                                          {breadcrumb.title}
                                        </Link>
                                      </BreadcrumbLink>
                                    </BreadcrumbItem>
                                    {index < breadcrumbs.length - 1 && (
                                      <BreadcrumbSeparator />
                                    )}
                                  </React.Fragment>
                                ))}
                              </BreadcrumbList>
                            </Breadcrumb>
                          </div>

                          <HeaderActions />
                        </header>

                        {config.isDev && isShipAndDebit && (
                          <div className="mx-2 flex items-center gap-2 rounded-md border border-yellow-300 bg-yellow-50 px-4 py-2 text-sm font-medium text-yellow-800">
                            <WarningIcon
                              className="size-4 shrink-0"
                              weight="fill"
                            />
                            <T context="warning banner shown in dev environment for ship-and-debit section">
                              This is a dev environment — data and actions here
                              do not affect production.
                            </T>
                          </div>
                        )}
                        {/* id is referenced by scrollToTopSelectors in index.tsx
                            so navigation resets this scroller, not the window. */}
                        <main
                          id="app-main-content"
                          className="flex-1 min-h-0 p-2 w-full min-w-0 overflow-x-hidden overflow-y-auto"
                        >
                          <ErrorBoundary>{children}</ErrorBoundary>
                        </main>
                        <FeedbackButton />
                      </SidebarInset>
                      <EntityQuickFind />
                      <FloatingAgentPanel />
                      {/* Fixed, bottom-right, and outside SidebarInset so work
                          started on any page keeps reporting after the user
                          navigates away from it. */}
                      <BackgroundJobsPanel />
                      {/* Fixed overlay — outside SidebarInset so it never
                          occupies layout space or pushes the page down, but
                          inside SidebarProvider so its minimized state can
                          inherit --sidebar-width and track the sidenav's
                          collapse. */}
                      <DeploymentEnvBanner />
                    </SidebarProvider>
                    <AnnotationCanvas />
                    <ChatAgentRenderer />
                  </AnnotationCanvasProvider>
                </HelpMenuProvider>
              </AgentContainerProvider>
            </TodoListProvider>
          </AgentStateProvider>
        </AgentPanelProvider>
      </AppProviders>
    </HotkeysProvider>
  );
}
